export const CONNECTION_LOST_ERROR = "Connection lost";

export type TransportFailure =
  | "connection_lost"
  | "not_connected"
  | "not_authenticated"
  | "request_timed_out"
  | "request_aborted"
  // A request outlived the session it was asked in (re-login, logout, endpoint switch): never sent.
  | "session_changed";

// Only the client's own transport throws these: a relay `cyborg:error` or a disconnect is an answer.
export class TransportError extends Error {
  readonly transport: TransportFailure;

  constructor(transport: TransportFailure, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "TransportError";
    this.transport = transport;
  }
}

export function isTransportFailure(err: unknown): err is TransportError {
  return err instanceof TransportError;
}

export function isOutcomeUnknown(err: unknown): boolean {
  return (
    isTransportFailure(err) &&
    (err.transport === "request_timed_out" || err.transport === "connection_lost")
  );
}

// A fetch() that got no response, told apart by name, never by wording: a timeout, an abort, or
// the TypeError the Fetch spec rejects a network error with. Anything else is not the network.
function fetchTransportError(err: unknown): TransportError | undefined {
  const name = (err as { name?: unknown } | null)?.name;
  if (name === "TimeoutError") {
    return new TransportError("request_timed_out", "HTTP request timed out", { cause: err });
  }
  if (name === "AbortError") {
    return new TransportError("request_aborted", "HTTP request aborted", { cause: err });
  }
  if (err instanceof TypeError) {
    return new TransportError("connection_lost", "HTTP request got no response", { cause: err });
  }
  return undefined;
}

// fetch() whose no-response rejection is a TransportError. The request is built first, so an
// invalid URL or configuration throws as itself (ours to fix), as does the caller's own abort.
export async function fetchOrTransportError(
  input: string | URL,
  init: RequestInit = {},
): Promise<Response> {
  // Throws a TypeError for a bad URL, method or header, as fetch would; resolved as a page resolves
  // it (off a page, as a relative path on a stand-in origin: fetch is the one to judge it there).
  void new Request(new URL(input, globalThis.location?.href ?? "http://localhost/"), init);
  try {
    return await fetch(input, init);
  } catch (err) {
    const reason = (init.signal?.reason as { name?: unknown } | undefined)?.name;
    if (init.signal?.aborted && reason !== "TimeoutError") throw err;
    throw fetchTransportError(err) ?? err;
  }
}
